import React, { useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { FileText } from 'lucide-react';
import { usePropertyCorrections } from '@/contexts/PropertyCorrectionsContext';

interface ReportPropertyErrorsProps {
  propertyId?: string;
  subjectProperty?: {
    livingAreaSqft?: number;
    buildingSqft?: number;
    garageSize?: string;
  };
}

const ReportPropertyErrors = ({
  propertyId,
  subjectProperty,
}: ReportPropertyErrorsProps) => {
  const { corrections, updateCorrections } = usePropertyCorrections();

  // Use corrections from context if available, otherwise fall back to subject property values
  const [livingAreaSqft, setLivingAreaSqft] = useState(
    corrections.livingAreaSqft?.toString() ||
      subjectProperty?.livingAreaSqft?.toString() ||
      '',
  );
  const [buildingSqft, setBuildingSqft] = useState(
    corrections.buildingSqft?.toString() ||
      subjectProperty?.buildingSqft?.toString() ||
      '',
  );
  const [garageSize, setGarageSize] = useState(
    corrections.garageSize || subjectProperty?.garageSize || '',
  );
  const [errorDescription, setErrorDescription] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    setIsSubmitting(true);

    // Prepare corrections object
    const newCorrections: {
      livingAreaSqft?: number;
      buildingSqft?: number;
      garageSize?: string;
      errorDescription?: string;
    } = {};

    if (
      livingAreaSqft &&
      livingAreaSqft !== subjectProperty?.livingAreaSqft?.toString()
    ) {
      newCorrections.livingAreaSqft = Number(livingAreaSqft);
    }

    if (
      buildingSqft &&
      buildingSqft !== subjectProperty?.buildingSqft?.toString()
    ) {
      newCorrections.buildingSqft = Number(buildingSqft);
    }

    if (garageSize && garageSize !== subjectProperty?.garageSize) {
      newCorrections.garageSize = garageSize;
    }

    if (errorDescription) {
      newCorrections.errorDescription = errorDescription;
    }

    // Update context with corrections
    updateCorrections(newCorrections);

    // Simulate a successful submission
    setTimeout(() => {
      toast({
        title: 'Report Submitted',
        description: 'Thank you for helping us improve our data!',
      });

      // Reset form
      setIsOpen(false);
      setIsSubmitting(false);
    }, 1000);

    window.localStorage.setItem(
      'propertyInfo',
      JSON.stringify({
        ...JSON.parse(window.localStorage.getItem('propertyInfo')),
        propertyCorrections: newCorrections,
      }),
    );
  };

  console.log(JSON.parse(window.localStorage.getItem('propertyInfo')));

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="link"
          className="p-0 h-auto text-tax-brightOrange font-medium text-base inline-flex items-center hover:text-tax-brightOrange/90 underline underline-offset-2"
        >
          <FileText className="h-4 w-4 mr-1" />
          Report Incorrect Property Details
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 bg-white shadow-lg" side="top">
        <div className="space-y-4">
          <h4 className="font-medium text-tax-deepBlue text-lg">
            Report Property Information Errors
          </h4>

          <div className="space-y-4">
            <div>
              <Label htmlFor="livingAreaSqft" className="text-sm font-medium">
                Actual Living Area Sqft:
              </Label>
              <Input
                id="livingAreaSqft"
                type="number"
                value={livingAreaSqft}
                onChange={(e) => setLivingAreaSqft(e.target.value)}
                placeholder="Enter actual living area sqft"
                className="border-tax-borderGray"
              />
            </div>

            <div>
              <Label htmlFor="buildingSqft" className="text-sm font-medium">
                Actual Building Sqft:
              </Label>
              <Input
                id="buildingSqft"
                type="number"
                value={buildingSqft}
                onChange={(e) => setBuildingSqft(e.target.value)}
                placeholder="Enter actual building sqft"
                className="border-tax-borderGray"
              />
            </div>

            <div>
              <Label htmlFor="garageSize" className="text-sm font-medium">
                Garage Size:
              </Label>
              <Input
                id="garageSize"
                type="text"
                value={garageSize}
                onChange={(e) => setGarageSize(e.target.value)}
                placeholder="Enter garage size"
                className="border-tax-borderGray"
              />
            </div>

            <div>
              <Label htmlFor="errorDescription" className="text-sm font-medium">
                Please give a detailed description of other errors to bring to
                the county's attention:
              </Label>
              <Textarea
                id="errorDescription"
                value={errorDescription}
                onChange={(e) => setErrorDescription(e.target.value)}
                placeholder="Please describe any other errors in detail..."
                className="min-h-[100px] border-tax-borderGray"
                maxLength={1000}
              />
              <div className="text-xs text-tax-neutralGray mt-1">
                {errorDescription.length}/1000 characters
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="border-tax-borderGray flex-1"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-primary hover:bg-primary/90 text-primary-foreground flex-1"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ReportPropertyErrors;
